import React, { Fragment } from 'react'
import { Switch } from '@material-ui/core'

// @src imports
import { AccountFragment, ExpenseReload } from 'src/graphql'
import { useActions, useMutations, usePropChange, useState } from 'src/hooks'
import { Button, Tooltip } from 'src/chrome'
import { formatAmount } from 'src/helpers/payments'
import {
  BaseCreditCard,
  EmptyCard,
  ExpenseBucketCard,
  ExpenseReloadForm,
} from 'src/payments/components'
import { FlexColumn } from 'src/styled'
// relative imports
import AddExpense from '../AddExpense/AddExpense'
import ExpensePurchaseModal from './ExpensePurchaseModal'

import styles from './styles'
import { Spacer, Text } from '@sendoutcards/quantum-design-ui'

type EditingState = 'none' | 'editing' | 'view'

const ExpenseBucket = (props: { account: AccountFragment }) => {
  const { account } = props
  const actions = useActions()
  const mutations = useMutations()
  const { expenseReload, expense } = account
  const [editingContext, setEditingContext] = useState<EditingState>(
    expenseReload || (expense !== null && expense.amount > 0) ? 'view' : 'none',
  )
  const [isSaving, setIsSaving] = useState(false)
  const [stripeOrderId, setStripeOrderId] = useState<string | null>(null)
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false)
  const [isPurchasingExpense, setIsPurchasingExpense] = useState(false)
  const [manualExpenseAmount, setManualExpenseAmount] = useState(0)
  const [purchaseAmount, setPurchaseAmount] = useState(
    expenseReload ? expenseReload.purchaseAmount / 100 : 10,
  )
  const [threshold, setThreshold] = useState(
    expenseReload ? expenseReload.threshold : 5,
  )
  const [isEnabled, setIsEnabled] = useState(
    expenseReload ? expenseReload.isEnabled || expenseReload.isRequired : false,
  )
  const currentExpenseReload = {
    purchaseAmount,
    threshold,
    isEnabled,
  } as ExpenseReload

  const handleChange = (
    name: 'purchaseAmount' | 'threshold',
    value: number,
  ) => {
    switch (name) {
      case 'purchaseAmount':
        setPurchaseAmount(value)
        break
      case 'threshold':
        setThreshold(value)
        break
    }
  }

  const handleClick = () => {
    if (isEnabled) {
      handleUpdateExpense()
    } else {
      purchaseExpense()
    }
    setEditingContext('view')
  }

  const handleUpdateExpense = async () => {
    setIsSaving(true)
    try {
      const {
        updateAccount: { account },
      } = await mutations.updateAccount({
        account: {
          expenseReload: {
            ...currentExpenseReload,
            threshold: threshold / 100,
          },
        },
      })
      setIsSaving(false)
      actions.updatedAccount(account)
    } catch (error) {
      setIsSaving(false)
    }
  }

  const purchaseExpense = async () => {
    const ITEM_CODE = '11132'
    setIsPurchasingExpense(true)
    const {
      createStripeOrder: { stripeOrder },
    } = await mutations.createStripeOrder({
      itemCode: ITEM_CODE,
      coupon: undefined,
      quantity: manualExpenseAmount,
    })
    setStripeOrderId(stripeOrder.id)
    setIsPurchaseModalOpen(true)
    setIsPurchasingExpense(false)
  }

  const submitStripeOrder = async () => {
    if (stripeOrderId) {
      setIsPurchasingExpense(true)
      try {
        const { purchaseProduct } = await mutations.purchaseProduct({
          orderId: stripeOrderId,
        })
        const { error } = purchaseProduct
        if (!!error) {
          console.error(error)
        } else {
          actions.updatedAccount(purchaseProduct.account)
          // If a user buys expense but doesn't have a bucket. Give it to 'em!
          if (!expenseReload) {
            handleUpdateExpense()
          }
          setIsPurchaseModalOpen(false)
          setIsPurchasingExpense(false)
          setStripeOrderId(null)
        }
      } catch (error) {
        setIsPurchasingExpense(false)
        console.error(error)
      }
    }
  }

  const getToolTipMessage = (noStripeSource: boolean) =>
    noStripeSource
      ? 'You must add a credit card to enable purchasing expense'
      : `Your bucket is too full to allow any refills at this time. Please check back when your bucket is less than $${formatAmount(
          5000,
        )}`

  usePropChange(isEnabled, handleUpdateExpense)

  const buttonTitle = () =>
    !isSaving && !isEnabled
      ? 'Purchase'
      : isPurchasingExpense
      ? 'Creating Order...'
      : isSaving
      ? 'Saving...'
      : 'Done'

  const expenseBalance = account.expense.amount
  const limitDifference = Math.ceil(50 - expenseBalance / 100)
  const manualExpenseLimit = Math.min(
    limitDifference > 0 ? limitDifference : 0,
    50,
  )
  const hasNoStripeSource = !account.stripeSource
  const isOverBucketLimit = expenseBalance > 5000
  const isDisabled = isOverBucketLimit || hasNoStripeSource
  const toolTipMessage = getToolTipMessage(hasNoStripeSource)

  return (
    <Fragment>
      {editingContext === 'editing' ? (
        <div css={{ position: 'relative', zIndex: 2 }}>
          <ExpenseBucketCard
            expenseReload={account.expenseReload || currentExpenseReload}
            balance={expenseBalance}
          />
          <div css={styles.formCard}>
            <FlexColumn>
              <div css={styles.flex}>
                <Text type="body" color="primaryHeading" lineHeight={2.3}>
                  Enable Auto-Reload
                </Text>
                <Switch
                  checked={isEnabled}
                  disabled={expenseReload?.isRequired}
                  onChange={() => setIsEnabled(!isEnabled)}
                />
              </div>
              <div css={styles.flex}>
                {isEnabled ? (
                  <ExpenseReloadForm
                    expenseReload={currentExpenseReload}
                    onChange={handleChange}
                  />
                ) : (
                  <AddExpense
                    expenseAmount={manualExpenseAmount}
                    expenseLimit={manualExpenseLimit}
                    onChange={setManualExpenseAmount}
                  >
                    {isDisabled && (
                      <div css={styles.disabled}>
                        <Text type="footnote">Disabled</Text>
                        <Spacer space="x_5" orientation="horizontal" />
                        <Tooltip hoverIcon={'INFO'} message={toolTipMessage} />
                      </div>
                    )}
                  </AddExpense>
                )}
              </div>
            </FlexColumn>
            <Button
              buttonColor={'pink'}
              title={buttonTitle()}
              disabled={isSaving || (!isEnabled && manualExpenseAmount === 0)}
              onClick={handleClick}
              css={styles.purchaseButton}
            />
          </div>
        </div>
      ) : editingContext === 'none' ? (
        <BaseCreditCard onClick={() => setEditingContext('editing')}>
          <EmptyCard message={'Add Expense Bucket'} />
        </BaseCreditCard>
      ) : editingContext === 'view' ? (
        <Fragment>
          <ExpenseBucketCard
            expenseReload={account.expenseReload ?? currentExpenseReload}
            balance={account.expense.amount}
          />
          <div css={styles.editExpenseContainer}>
            <Button
              onClick={() => setEditingContext('editing')}
              title={'Manage Expense'}
            />
          </div>
        </Fragment>
      ) : null}
      {isPurchaseModalOpen && (
        <ExpensePurchaseModal
          onPurchase={submitStripeOrder}
          onClose={() => setIsPurchaseModalOpen(false)}
          amount={manualExpenseAmount}
          isPurchasing={isPurchasingExpense}
        />
      )}
    </Fragment>
  )
}

export default ExpenseBucket
